import React, { FC, useEffect, useRef, useState } from 'react';
import attachImage from 'assets/svg/attach file.svg';
import sendImage from 'assets/svg/send_white.svg';
import Styles from 'components/chatContent/chatFooter/ChatFooter.module.scss';
import { randomString, getCurrentDate } from 'helpers/utils';
import { sendFCMMessage, sendThreadMessage, updateNewRoomStatus, uploadImage } from 'store/services/firebase-broadcast';
import { Attachment } from 'modules/types/types';
import { attachment } from 'helpers/data/general';
import moment from 'moment';
import { invitePeople, invitePeopleRequest } from 'store/actions/general.actions';
import { connect } from 'react-redux';

interface ChatFooterProps {
  typing: Function;
  selected: any;
  chats?: any;
  isThread?: boolean;
  parentMessage?: object;
  images?: Function;
  clearImages?: Function;
  activateRoom?: any;
  invitePeople: Function;
  attachedImages?: any;
  onMessageSent?: () => void;
}

const ChatFooter: FC<ChatFooterProps> = (props: ChatFooterProps) => {
  const { typing, selected, chats, isThread, parentMessage, images, clearImages, activateRoom, invitePeople, attachedImages, onMessageSent } = props;
  const [file, setFile] = useState<Attachment>(attachment);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<any>([]);
  const [send, setSend] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(40);
  const [footerBottom, setFooterBottom] = useState(0);
  const inputRef: any = useRef(null);
  const textInputRef: any = useRef(null);
  const chatFooterRef: any = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [sender] = useState({
    id: user?.pk,
    name: user.name,
    image: user.profile,
    isHost: user?.pk === selected.host?.pk,
  });

  const handleMessage = async () => {
    if (isThread) {
      await sendThread();
    } else {
      await sendMessage();
      let updateRoom = {
        room:activateRoom,
        activeRoom: moment().format()
      };
      updateNewRoomStatus(updateRoom)
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim().length > 0 || attachments.length > 0) {
        handleMessage();
      }
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const cursorPosition = e.currentTarget.selectionStart;
      if (cursorPosition !== null) {
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + '\n' + textAfterCursor;
        setMessage(newMessage);
        
        // Set cursor position after the new line using a more reliable approach
        setTimeout(() => {
          const inputElement = textInputRef.current;
          if (inputElement && inputElement.setSelectionRange) {
            try {
              inputElement.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
              inputElement.focus();
            } catch (error) {
              console.warn('Failed to set cursor position:', error);
            }
          }
        }, 10);
      }
    }
  };

  const handleAttachment = (e: any) => {
    if (e.target.files.length > 0) {
      let type = 'image';
      let fileType = e.target.files[0].type;
      fileType = fileType.split('/');
      if (fileType[0] === 'video') type = 'video';
      const obj = {
        id: `${selected?.pk}-${randomString()}`,
        name: e.target.files[0].name,
        path: `${URL.createObjectURL(e.target.files[0])}`,
        file: e.target.files[0],
        type: type,
      };
      setFile(obj);
      setAttachments([...attachments, obj.path]);
      images && images(obj);
    }
  };

  const sendMessage = async () => {
    const msg = {
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: message,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [file],
      threads: [],
    };
    
    try {
      // If there's a file attachment, upload it first, then send the message
      if (file.id !== '') {
        await uploadImage(msg, file);
        await sendFCMMessage(msg);
      } else {
        // No attachment, send message immediately
        await sendFCMMessage(msg);
      }
      
      // Only clear state if message was sent successfully
      setMessage('');
      setFile(attachment);
      setAttachments([]); // Clear the attachments array
      
      // Clear the images in the parent component to clear attachment list
      if (clearImages && typeof clearImages === 'function') {
        clearImages();
      }

      // Notify parent to scroll to bottom
      onMessageSent && onMessageSent();
    } catch (error: any) {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      console.error('Failed to send message:', error);
    }
  };

  const sendThread = async () => {
    const { id }: any = parentMessage;
    const msg = {
      parentId: id,
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: message,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [file], // Use the selected file instead of empty array
      threads: [],
    };
    
    try {
      // If there's a file attachment, upload it first, then send the thread message
      if (file.id !== '') {
        await uploadImage(msg, file);
        await sendThreadMessage(msg);
      } else {
        // No attachment, send thread message immediately
        await sendThreadMessage(msg);
      }
      
      // Only clear state if message was sent successfully
      setMessage('');
      setFile(attachment);
      setAttachments([]); // Clear the attachments array
      
      // Clear the images in the parent component to clear attachment list
      if (clearImages && typeof clearImages === 'function') {
        clearImages();
      }

      // Notify parent to scroll to bottom
      onMessageSent && onMessageSent();
    } catch (error: any) {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      console.error('Failed to send thread message:', error);
    }
  };

  useEffect(() => {
    if (message.length > 0) {
      setSend(true);
      typing(true);
    } else {
      setSend(false);
      typing(false);
    }
  }, [message]);

  useEffect(() => {
    // Add null check for attachedImages before calling filter
    if (attachedImages && Array.isArray(attachedImages)) {
      const noFile = attachedImages.filter((i:any) => i.file === inputRef?.current?.files[0]);
      if (noFile.length === 0) {
        inputRef.current.value = '';
      }
    }
  }, [attachedImages])

  // Handle mobile keyboard visibility with Visual Viewport API
  useEffect(() => {
    // Only run on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    if (!isMobile || !window.visualViewport) return;

    const handleViewportChange = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;
      
      const windowHeight = window.innerHeight;
      const viewportHeight = viewport.height;
      
      // Calculate how much the keyboard is taking up
      const keyboardHeight = windowHeight - viewportHeight;
      
      // Adjust footer position to stay above keyboard
      if (keyboardHeight > 100) {
        // Keyboard is showing
        setFooterBottom(keyboardHeight);
        
        // Scroll textarea into view after a small delay
        setTimeout(() => {
          if (textInputRef.current && document.activeElement === textInputRef.current) {
            textInputRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
      } else {
        // Keyboard is hidden
        setFooterBottom(0);
      }
    };

    // Listen to both resize and scroll events
    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);

    // Initial check
    handleViewportChange();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleViewportChange);
    };
  }, []);

  // Scroll textarea into view when focused
  useEffect(() => {
    const textarea = textInputRef.current;
    if (!textarea) return;

    const handleFocus = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
      if (!isMobile) return;

      // Multiple scroll attempts to handle various keyboard opening speeds
      const scrollIntoView = () => {
        textarea.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      };

      scrollIntoView();
      setTimeout(scrollIntoView, 300);
      setTimeout(scrollIntoView, 600);
    };

    textarea.addEventListener('focus', handleFocus);

    return () => {
      textarea.removeEventListener('focus', handleFocus);
    };
  }, []);
  
  // Scroll textarea so the cursor stays visible when content overflows
  const scrollCursorIntoView = (textarea: HTMLTextAreaElement) => {
    const cursorPos = textarea.selectionStart;
    if (cursorPos === null) return;

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 24;
    const paddingTop = parseFloat(style.paddingTop) || 8;

    // Use a mirror div off-screen to get the pixel offset of the cursor from the top of the content
    const mirror = document.createElement('div');
    mirror.style.cssText = `
      position: fixed; left: -9999px; top: 0; visibility: hidden; white-space: pre-wrap; word-wrap: break-word;
      font: ${style.font}; padding: ${style.padding}; width: ${textarea.clientWidth}px;
      box-sizing: border-box; line-height: ${style.lineHeight};
    `;
    mirror.textContent = textarea.value.substring(0, cursorPos);
    document.body.appendChild(mirror);
    const cursorOffsetFromTop = mirror.offsetHeight - paddingTop;
    document.body.removeChild(mirror);

    const visibleTop = textarea.scrollTop;
    const visibleBottom = textarea.scrollTop + textarea.clientHeight;
    const cursorLineTop = cursorOffsetFromTop;
    const cursorLineBottom = cursorOffsetFromTop + lineHeight;

    if (cursorLineBottom > visibleBottom) {
      textarea.scrollTop = cursorLineBottom - textarea.clientHeight + paddingTop;
    } else if (cursorLineTop < visibleTop) {
      textarea.scrollTop = Math.max(0, cursorLineTop - paddingTop);
    }
  };

  // Auto-resize textarea based on content with better height management
  useEffect(() => {
    if (textInputRef.current) {
      const textarea = textInputRef.current;
      
      // Get computed styles to calculate accurate line height
      const computedStyle = window.getComputedStyle(textarea);
      // Get line-height - prefer inline style value (24px) for consistency
      let lineHeight = parseFloat(computedStyle.lineHeight);
      // If line-height seems too small (likely from CSS override), use explicit 24px
      if (isNaN(lineHeight) || lineHeight < 20) {
        lineHeight = 24; // Explicit 24px as set in inline style
      }
      
      const paddingTop = parseFloat(computedStyle.paddingTop) || 8;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 8;
      
      const maxLines = 2; // Max 3 lines
      // Calculate max height for exactly 3 lines: line height * maxLines + top padding + bottom padding
      // Use Math.floor to ensure we never exceed 3 lines
      const maxHeight = Math.floor((lineHeight * maxLines) + paddingTop + paddingBottom);
      
      // Set max-height FIRST to prevent rendering beyond 3 lines
      textarea.style.maxHeight = `${maxHeight}px`;
      
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate new height with constraints (max 3 lines, but at least minHeight)
      const minHeight = 40;
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      
      // Update state for height tracking
      setTextareaHeight(newHeight);
      
      // ALWAYS enforce maxHeight - never allow height to exceed 3 lines
      const finalHeight = Math.min(newHeight, maxHeight);
      textarea.style.height = `${finalHeight}px`;
      
      // Show scrollbar when content exceeds max height (3 lines)
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
        // CRITICAL: Force height to exactly maxHeight when scrolling
        textarea.style.height = `${maxHeight}px`;
        textarea.style.maxHeight = `${maxHeight}px`;
      } else {
        textarea.style.overflowY = 'hidden';
      }
      textarea.style.overflowX = 'hidden';
      
      // Ensure the parent container adjusts smoothly
      if (textarea.parentElement) {
        textarea.parentElement.style.transition = 'height 0.2s ease';
      }

      // Keep cursor in view when textarea content scrolls
      scrollCursorIntoView(textarea);
      // Run again after a short delay (e.g. after Shift+Enter sets cursor in setTimeout)
      const t = setTimeout(() => {
        if (textInputRef.current) scrollCursorIntoView(textInputRef.current);
      }, 25);
      return () => clearTimeout(t);
    }
  }, [message]);
  return (
    <div 
      ref={chatFooterRef}
      className={`${Styles.ChatFooter}`} 
      style={{ 
        bottom: footerBottom > 0 ? `${footerBottom}px` : undefined 
      }}
    >
      <div className={`${Styles.messageBox} position-relative`} style={{ 
        minHeight: '40px',
        height: `${textareaHeight}px`,
        transition: 'height 0.2s ease'
      }}>
        <div className={Styles.attachButtonWrapper}>
          <div className={`position-relative ${Styles.attachButton}`}>
            <input ref={inputRef} type="file" onChange={handleAttachment} />
            <img src={attachImage} alt="attach file" />
          </div>
        </div>
        
        {/* Text input area */}
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <textarea
            ref={textInputRef}
            value={message}
            placeholder="Type your message…"
            className={`w-100 ${Styles.typingArea}`}
            onChange={(e) => {
              setMessage(e.target.value);
              setTimeout(() => {
                if (textInputRef.current) scrollCursorIntoView(textInputRef.current);
              }, 0);
            }}
            onKeyDown={handleKeyDown}
            rows={1}
            style={{ 
              resize: 'none', 
              overflow: 'hidden',
              overflowX: 'hidden',
              minHeight: '40px',
              maxHeight: '88px', // Strictly enforce 3 lines: (24px * 3) + 8px top + 8px bottom = 88px
              lineHeight: '24px', // Explicit line-height: 24px for 3 lines = 72px + 16px padding = 88px max
              padding: '8px 12px',
              transition: 'height 0.2s ease',
              boxSizing: 'border-box',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              position: 'relative',
              zIndex: 1,
              fontSize: '12px' // Set to 12px
            }}
          />
        </div>
      </div>
      {send || attachments.length > 0 ? (
        <div onClick={handleMessage} onKeyPress={handleMessage} role="button" tabIndex={0}>
          <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
        </div>
      ) : (
        <div>
          <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
        </div>
      )}
    </div>
  );
};
const mapDispatchToProps = { invitePeople: invitePeopleRequest };
export default connect(null, mapDispatchToProps)(ChatFooter);
